import {sendEventToTrackingServer} from '@utils/tracking';
import ValidatingInputElement from '@validation/ValidatingInputElement';
import type {HTMLOcTextAreaV1Element} from '@otto-ec/otto-components/text-area';
import type {HTMLOcTextFieldV1Element} from '@otto-ec/otto-components/text-field';

class FeedbackStartPage {

    static readonly SHOW_STEP2_CSS_CLASS = 'con_feedback-start__form--step2'

    private readonly $documentObject: Document
    private readonly $form: HTMLFormElement | null
    private readonly $browserInformationField: HTMLInputElement | null
    private readonly $osInformationField: HTMLInputElement | null
    private readonly $deviceTypeInformationField: HTMLInputElement | null
    private readonly $step1: HTMLDivElement | null
    private readonly $step2: HTMLDivElement | null
    private readonly $step1Checkboxes: NodeListOf<HTMLInputElement> | null
    private readonly $step2Radios: NodeListOf<HTMLInputElement> | null
    private readonly $step1Message: HTMLOcTextAreaV1Element | null
    private readonly messageValidator: ValidatingInputElement | null
    private readonly $step1SubmitButton: HTMLButtonElement | null
    private readonly $step2EmailInput: HTMLOcTextFieldV1Element | null
    private readonly emailValidator: ValidatingInputElement | null
    private readonly $step2HelpLink: HTMLLinkElement | null
    private readonly $step2BackButton: HTMLButtonElement | null

    private constructor(documentObject: Document) {
        this.$documentObject = documentObject
        this.$form = this.$documentObject.querySelector('[data-js-hook="con_feedback__form"]')
        this.$step1 = this.$form?.querySelector('[data-js-hook="con_feedback-step1"]') || null
        this.$step2 = this.$form?.querySelector('[data-js-hook="con_feedback-step2"]') || null
        this.$step1Checkboxes = this.$step1?.querySelectorAll('[data-js-hook="con_feedback-step1__checkbox"]') || null
        this.$step2Radios = this.$step2?.querySelectorAll('[data-js-hook="con_feedback-step2__contact-radioselect"]') || null
        this.$step1Message = this.$step1?.querySelector('[data-js-hook="con_feedback-step1__message"]') || null
        this.messageValidator = ValidatingInputElement.initialize(this.$step1Message, () => this.handleStep1ErrorsAndReturnValidity())
        this.$step1SubmitButton = this.$form?.querySelector('[data-js-hook="con_feedback-step1__submit-button"]') || null

        this.$step2EmailInput = this.$form?.querySelector('[data-js-hook="con_feedback-step2__email-input"]') || null
        this.emailValidator = ValidatingInputElement.initialize(this.$step2EmailInput, () => {})
        this.updateEmailOptionality()

        this.$step2HelpLink = this.$form?.querySelector('[data-js-hook="con_feedback-step2__help-link"]') || null
        this.$step2BackButton = this.$form?.querySelector('[data-js-hook="con_feedback-step2__back-button"]') || null

        this.$browserInformationField = this.$form?.querySelector('[data-js-hook="con_feedback__form__browser"]') || null
        this.$osInformationField = this.$form?.querySelector('[data-js-hook="con_feedback__form__os"]') || null
        this.$deviceTypeInformationField = this.$form?.querySelector('[data-js-hook="con_feedback__form__device-type"]') || null

        this.$browserInformationField && (this.$browserInformationField.value = window.o_global.device.browser)
        this.$osInformationField && (this.$osInformationField.value = window.o_global.device.os)
        this.$deviceTypeInformationField && (this.$deviceTypeInformationField.value = window.o_global.device.type)
    }

    private bindFormErrorHandling(): void {
        this.$step1Checkboxes && Array.from(this.$step1Checkboxes).forEach( checkbox =>  checkbox.addEventListener('change', () => this.handleStep1ErrorsAndReturnValidity()))
        this.$step2Radios && Array.from(this.$step2Radios).forEach( radio =>  radio.addEventListener('change', () => this.updateEmailOptionality()))
        this.$form?.addEventListener('submit', (event) => {
            if(!(this.emailValidator?.revealErrorForSubmit() ?? true)) {
                event.preventDefault()
            }
        })
        this.handleStep1ErrorsAndReturnValidity()
    }

    private handleStep1ErrorsAndReturnValidity(): boolean {
        let step1ok = false
        if(this.$step1SubmitButton) {
            const isAnyCheckboxSelected = this.$form?.querySelectorAll('[data-js-hook="con_feedback-step1__checkbox"]:checked').length != 0
            const messageIsValid = this.messageValidator?.isValid ?? true
            step1ok = isAnyCheckboxSelected && messageIsValid
            this.$step1SubmitButton.disabled = !step1ok
        }
        return step1ok
    }

    private updateEmailOptionality(): void {
        this.emailValidator?.setOptional(!this.isContactWanted())
    }

    private isContactWanted(): boolean {
        return !!this.$step2Radios && Array.from(this.$step2Radios).some(radio => radio.dataset.jsContactwanted == 'true' && radio.checked)
    }

    private initStepMaker(): void {
        addEventListener('hashchange', () => {
            this.displayStepFromUri()
        }, false)
        this.$step1SubmitButton && this.$step1SubmitButton.addEventListener('click', () => this.gotoStep('#step2'))
        this.$step2BackButton && this.$step2BackButton.addEventListener('click', () => this.gotoStep('#step1'))
        this.$form && this.$form.dataset.jsShowstep2 && this.gotoStep('#step2')
        if(this.getStepFromUri() == '#step2') {
            this.handleStep1ErrorsAndReturnValidity()
                ? this.displayStepFromUri()
                : this.gotoStep('#step1')
        }
    }

    private displayStepFromUri(): void {
        const currentStep = this.getStepFromUri()
        if(currentStep == '#step2') {
            this.$form?.classList.add(FeedbackStartPage.SHOW_STEP2_CSS_CLASS)
        } else {
            this.$form?.classList.remove(FeedbackStartPage.SHOW_STEP2_CSS_CLASS)
        }
    }

    private getStepFromUri(): FeedbackStep {
        return availableFeedbackSteps.indexOf(<FeedbackStep>location.hash) > -1 ? <FeedbackStep>location.hash : '#step1'
    }

    private gotoStep(step: FeedbackStep): void {
        location.hash = step
        this.trackHelpLinkOnStep2(step)
    }

    private trackHelpLinkOnStep2(step: FeedbackStep): void {
        if(step === '#step2') {
            sendEventToTrackingServer({
                contact_Activity: 'show',
                contact_Channel: 'contactflow',
                contact_Source: 'praise_and_criticism'
            })
        }
    }

    private registerClickTracking(): void {
        if(this.$step2HelpLink) {
            this.$step2HelpLink.addEventListener('click', () => {
                sendEventToTrackingServer({
                    contact_Activity: 'click',
                    contact_Channel: 'contactflow',
                    contact_Source: 'praise_and_criticism'
                })
            })
        }
    }

    static initialize(documentObject: Document): FeedbackStartPage {
        const feedbackStartPage: FeedbackStartPage = new FeedbackStartPage(documentObject)
        feedbackStartPage.initStepMaker()
        feedbackStartPage.bindFormErrorHandling()
        feedbackStartPage.registerClickTracking()
        return feedbackStartPage
    }
}
export default FeedbackStartPage

const availableFeedbackSteps = ['#step1', '#step2'] as const
type FeedbackStep = typeof availableFeedbackSteps[number]